"use client";

/**
 * Tenant settings hook
 * Re-exports from the original file with updated import path
 */

import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/hooks/auth/use-auth";
import {
  type EconomicsSettings,
  DEFAULT_ECONOMICS_SETTINGS,
} from "@/components/shopify/economics/types";
import { useTranslations } from "next-intl"


export interface TenantSetting {
  id: string;
  tenant_id: string;
  namespace: string;
  name: string;
  description?: string | null;
  config: Record<string, any>;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export function useTenantSettings(namespace?: string) {
  const tUi = useTranslations("Feedback.SettingsUseTenant")

  const { fetchWithAuth, isAuthenticated } = useAuth();
  const [settings, setSettings] = useState<TenantSetting[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = async () => {
    if (!isAuthenticated) return;

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (namespace) params.append("namespace", namespace);

      const query = params.toString();
      const url =
        query.length > 0
          ? `/api/tenant-settings?${query}`
          : "/api/tenant-settings";

      const response = await fetchWithAuth(url, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to fetch tenant settings");
      }

      setSettings(data.settings || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : tUi("unknownError"));
    } finally {
      setLoading(false);
    }
  };

  const createSetting = async (
    setting: Omit<
      TenantSetting,
      "id" | "tenant_id" | "created_at" | "updated_at"
    >,
  ) => {
    if (!isAuthenticated) throw new Error(tUi("userNotAuthenticated"));

    setLoading(true);
    setError(null);

    try {
      const response = await fetchWithAuth("/api/tenant-settings", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(setting),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to create tenant setting");
      }

      await fetchSettings();
      return data.setting;
    } catch (err) {
      setError(err instanceof Error ? err.message : tUi("unknownError"));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = async (id: string, updates: Partial<TenantSetting>) => {
    if (!isAuthenticated) throw new Error(tUi("userNotAuthenticated"));

    setLoading(true);
    setError(null);

    try {
      const response = await fetchWithAuth(`/api/tenant-settings/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updates),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to update tenant setting");
      }

      await fetchSettings();
      return data.setting;
    } catch (err) {
      setError(err instanceof Error ? err.message : tUi("unknownError"));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteSetting = async (id: string) => {
    if (!isAuthenticated) throw new Error(tUi("userNotAuthenticated"));

    setLoading(true);
    setError(null);

    try {
      const response = await fetchWithAuth(`/api/tenant-settings/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to delete tenant setting");
      }

      await fetchSettings();
      return data;
    } catch (err) {
      setError(err instanceof Error ? err.message : tUi("unknownError"));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchSettings();
  }, [isAuthenticated, namespace]);

  return {
    settings,
    loading,
    error,
    refetch: fetchSettings,
    createSetting,
    updateSetting,
    deleteSetting,
  };
}

// Specialized hooks - keeping them in the same file for simplicity

export interface EmailAutoReplySettings {
  enabled: boolean;
  selected_categories: string[];
  min_confidence: number;
  dry_run: boolean;
  create_draft_instead_of_send: boolean;
  start_date: string;
  create_crm_note: boolean;
  use_ai_tools: boolean;
  handover_enabled: boolean;
  handover_pipeline_id?: string;
  handover_status_id?: string;
  email_forward_enabled: boolean;
  email_forward_create_crm_note: boolean;
}

const defaultEmailAutoReplySettings: EmailAutoReplySettings = {
  enabled: false,
  selected_categories: [],
  min_confidence: 70,
  dry_run: true,
  create_draft_instead_of_send: false,
  start_date: new Date().toISOString().split("T")[0],
  create_crm_note: true,
  use_ai_tools: true,
  handover_enabled: false,
  handover_pipeline_id: undefined,
  handover_status_id: undefined,
  email_forward_enabled: false,
  email_forward_create_crm_note: true,
};

export function useEmailAutoReplySettings() {
  const tUi = useTranslations("Feedback.SettingsUseTenant")

  const { settings, loading, error, refetch, createSetting, updateSetting } =
    useTenantSettings("email_auto_reply");
  const { isAuthenticated } = useAuth();

  const getEmailAutoReplySettings = (): EmailAutoReplySettings => {
    const setting = settings.find((s) => s.namespace === "email_auto_reply");
    const rawConfig = setting?.config as Record<string, any> | undefined;

    if (!rawConfig) {
      return defaultEmailAutoReplySettings;
    }

    return {
      enabled: rawConfig.enabled ?? defaultEmailAutoReplySettings.enabled,
      selected_categories: rawConfig.selected_categories ?? defaultEmailAutoReplySettings.selected_categories,
      min_confidence: rawConfig.min_confidence ?? defaultEmailAutoReplySettings.min_confidence,
      dry_run: rawConfig.dry_run ?? defaultEmailAutoReplySettings.dry_run,
      create_draft_instead_of_send: rawConfig.create_draft_instead_of_send ?? defaultEmailAutoReplySettings.create_draft_instead_of_send,
      start_date: rawConfig.start_date ?? defaultEmailAutoReplySettings.start_date,
      // Support both new and legacy field names
      create_crm_note: rawConfig.create_crm_note ?? rawConfig.create_kommo_note ?? defaultEmailAutoReplySettings.create_crm_note,
      use_ai_tools: rawConfig.use_ai_tools ?? defaultEmailAutoReplySettings.use_ai_tools,
      handover_enabled: rawConfig.handover_enabled ?? rawConfig.escalation_enabled ?? defaultEmailAutoReplySettings.handover_enabled,
      handover_pipeline_id: rawConfig.handover_pipeline_id ?? rawConfig.escalation_pipeline_id ?? defaultEmailAutoReplySettings.handover_pipeline_id,
      handover_status_id: rawConfig.handover_status_id ?? rawConfig.escalation_status_id ?? defaultEmailAutoReplySettings.handover_status_id,
      email_forward_enabled: rawConfig.email_forward_enabled ?? defaultEmailAutoReplySettings.email_forward_enabled,
      // Support both new and legacy field names
      email_forward_create_crm_note: rawConfig.email_forward_create_crm_note ?? rawConfig.email_forward_create_kommo_note ?? defaultEmailAutoReplySettings.email_forward_create_crm_note,
    };
  };

  const updateEmailAutoReplySettings = async (
    updates: Partial<EmailAutoReplySettings>,
  ) => {
    if (!isAuthenticated) throw new Error(tUi("userNotAuthenticated"));

    const setting = settings.find((s) => s.namespace === "email_auto_reply");
    const newConfig = {
      ...defaultEmailAutoReplySettings,
      ...getEmailAutoReplySettings(),
      ...updates,
    };

    if (setting) {
      return updateSetting(setting.id, { config: newConfig });
    } else {
      return createSetting({
        namespace: "email_auto_reply",
        name: "email_auto_reply",
        config: newConfig,
        enabled: newConfig.enabled,
        description: tUi("autoReplySettingsForClassifiedEmails"),
      });
    }
  };

  const memoizedSettings = useMemo(() => getEmailAutoReplySettings(), [settings]);

  return {
    settings: memoizedSettings,
    loading,
    error,
    refetch,
    updateSettings: updateEmailAutoReplySettings,
  };
}

export function useBodyIdentityOverrideSettings() {
  return useTenantSettings("body_identity_override");
}

export interface ShopifyAttributionSettings {
  minimum_order_total: number;
}

const defaultShopifyAttributionSettings: ShopifyAttributionSettings = {
  minimum_order_total: 0,
};

export function useShopifyAttributionSettings() {
  const tUi = useTranslations("Feedback.SettingsUseTenant")

  const { settings, loading, error, refetch, createSetting, updateSetting } =
    useTenantSettings("shopify_attribution");
  const { isAuthenticated } = useAuth();

  const getShopifyAttributionSettings = (): ShopifyAttributionSettings => {
    const setting = settings.find((s) => s.namespace === "shopify_attribution");
    return (
      (setting?.config as ShopifyAttributionSettings) ||
      defaultShopifyAttributionSettings
    );
  };

  const updateShopifyAttributionSettings = async (
    updates: Partial<ShopifyAttributionSettings>,
  ) => {
    if (!isAuthenticated) throw new Error(tUi("userNotAuthenticated"));

    const setting = settings.find((s) => s.namespace === "shopify_attribution");
    const newConfig = {
      ...defaultShopifyAttributionSettings,
      ...getShopifyAttributionSettings(),
      ...updates,
    };

    if (setting) {
      return updateSetting(setting.id, { config: newConfig });
    }

    return createSetting({
      namespace: "shopify_attribution",
      name: "shopify_attribution",
      config: newConfig,
      enabled: true,
      description: tUi("preferencesForShopifyAttributionDashboardFilters"),
    });
  };

  const memoizedSettings = useMemo(() => getShopifyAttributionSettings(), [settings]);

  return {
    settings: memoizedSettings,
    loading,
    error,
    refetch,
    updateSettings: updateShopifyAttributionSettings,
  };
}

export type { EconomicsSettings };

const defaultEconomicsSettings: EconomicsSettings = DEFAULT_ECONOMICS_SETTINGS;

export function useEconomicsSettings() {
  const tUi = useTranslations("Feedback.SettingsUseTenant")

  const { settings, loading, error, refetch, createSetting, updateSetting } =
    useTenantSettings("shopify_economics");
  const { isAuthenticated } = useAuth();

  const getEconomicsSettings = (): EconomicsSettings => {
    const setting = settings.find((s) => s.namespace === "shopify_economics");
    return {
      ...defaultEconomicsSettings,
      ...(setting?.config as Partial<EconomicsSettings>),
    };
  };

  const updateEconomicsSettings = async (
    updates: Partial<EconomicsSettings>,
  ) => {
    if (!isAuthenticated) throw new Error(tUi("userNotAuthenticated"));

    const setting = settings.find((s) => s.namespace === "shopify_economics");
    const newConfig = {
      ...defaultEconomicsSettings,
      ...getEconomicsSettings(),
      ...updates,
    };

    if (setting) {
      return updateSetting(setting.id, { config: newConfig });
    }

    return createSetting({
      namespace: "shopify_economics",
      name: "shopify_economics",
      config: newConfig,
      enabled: true,
      description: tUi("productEconomicsGlobalParameters"),
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- same pattern as other settings hooks
  const memoizedSettings = useMemo(() => getEconomicsSettings(), [settings]);

  return {
    settings: memoizedSettings,
    loading,
    error,
    refetch,
    updateSettings: updateEconomicsSettings,
  };
}
